import React, {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import "./App.css";

import {ArchiveItem, ArchiveRecord, ConfigResponse, EmailLog, IndexResponse} from "./types";
import {formatValue, getStatusColor} from "./utils/format";
import {normalizeImagesByStation} from "./utils/images";
import {parseOutputJson, RawOutput} from "./utils/output";
import {ArchiveView} from "./components/ArchiveView";
import {EmailModal} from "./components/EmailModal";
import {ImageCarousel} from "./components/ImageCarousel";
import {SynopticPanel} from "./components/SynopticPanel";

const API_BASE_URL = "";

const App: React.FC = () => {
    const [outputStatus, setOutputStatus] = useState<ArchiveRecord | null>(null);
    const [outputError] = useState<string>("");
    const [archiveItems, setArchiveItems] = useState<ArchiveItem[]>([]);
    const [archiveError, setArchiveError] = useState<string>("");
    const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
    const [recordsById, setRecordsById] = useState<Record<string, ArchiveRecord | undefined>>({});
    const [dayStateByKey, setDayStateByKey] = useState<
        Record<string, {loading: boolean; loaded: boolean; error: string} | undefined>
    >({});

    const [radarThreshold, setRadarThreshold] = useState<number>(50);
    const [sensorsThreshold, setSensorsThreshold] = useState<number>(50);
    const [mediaThreshold, setMediaThreshold] = useState<number>(50);

    const toMediaUrl = (p: string) => {
        const raw = (p || "").trim();
        if (!raw) {
            return "";
        }
        if (raw.startsWith("http://") || raw.startsWith("https://")) {
            return raw;
        }
        if (raw.startsWith("/")) {
            return `${API_BASE_URL}${raw}`;
        }
        return `${API_BASE_URL}/${raw}`;
    };

    const openLinksPage = (title: string, links: string[]) => {
        const safeLinks = links.filter((x) => typeof x === "string" && x.trim().length > 0);
        const w = window.open("about:blank", "_blank");
        if (!w) {
            alert("Popup bloccato dal browser: abilita i popup per aprire la lista link.");
            return;
        }
        const escapeHtml = (s: string) =>
            s
                .replaceAll("&", "&amp;")
                .replaceAll("<", "&lt;")
                .replaceAll(">", "&gt;")
                .replaceAll('"', "&quot;")
                .replaceAll("'", "&#039;");
        const toApiHref = (p: string) => toMediaUrl(p);
        const toBasename = (p: string) => {
            const parts = p.split("/");
            return parts.length ? parts[parts.length - 1] : p;
        };
        const items = safeLinks
            .map((p) => {
                const label = escapeHtml(toBasename(p));
                const titleAttr = escapeHtml(p);
                const href = escapeHtml(toApiHref(p));
                return `<li><a href="${href}" target="_blank" rel="noreferrer" title="${titleAttr}">${label}</a></li>`;
            })
            .join("");
        const html = `<!doctype html>
<html lang="it">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(title)}</title>
    <style>
      body { font-family: sans-serif; margin: 24px; }
      h1 { margin: 0 0 12px 0; font-size: 18px; }
      p { margin: 0 0 14px 0; color: #555; }
      ul { padding-left: 18px; }
      li { margin: 6px 0; word-break: break-word; }
      a { color: #0b66c3; text-decoration: none; }
      a:hover { text-decoration: underline; }
      code { background: #f3f4f6; padding: 2px 6px; border-radius: 6px; }
    </style>
  </head>
  <body>
    <h1>${escapeHtml(title)}</h1>
    <p>Numero elementi: <code>${safeLinks.length}</code></p>
    <ul>${items}</ul>
  </body>
</html>`;
        try {
            w.opener = null;
        } catch (e) {
            //
        }
        try {
            w.document.open();
            w.document.write(html);
            w.document.close();
        } catch (e) {
            try {
                w.location.href = "about:blank";
            } catch (err) {
                //
            }
            alert(
                "Impossibile scrivere la pagina dei link (probabile restrizione del browser). Prova ad abilitare i popup e riprovare."
            );
        }
    };

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await axios.get<ConfigResponse>(`${API_BASE_URL}/media/config.json`);
                const cfg = res.data || {};
                if (typeof cfg.soglia_radar === "number" && Number.isFinite(cfg.soglia_radar)) {
                    setRadarThreshold(cfg.soglia_radar);
                }
                if (typeof cfg.soglia_sensori === "number" && Number.isFinite(cfg.soglia_sensori)) {
                    setSensorsThreshold(cfg.soglia_sensori);
                }
                if (typeof cfg.soglia_media === "number" && Number.isFinite(cfg.soglia_media)) {
                    setMediaThreshold(cfg.soglia_media);
                }
            } catch (e) {
                console.error(e);

            }
        };
        fetchConfig();
    }, []);

    useEffect(() => {
        const fetchOutputById = async (id: string, index: IndexResponse): Promise<ArchiveRecord | null> => {
            const outputPath = index[id]?.output_path;
            if (!outputPath) {
                return null;
            }
            const url = toMediaUrl(outputPath);
            if (!url) {
                return null;
            }
            const outRes = await axios.get<RawOutput>(url);
            const out = parseOutputJson(outRes.data, url);
            if (!out) {
                return null;
            }
            const record: ArchiveRecord = {id, ...out};
            setRecordsById((prev) => (prev[id] ? prev : {...prev, [id]: record}));
            return record;
        };

        const fetchArchive = async () => {
            try {
                const indexRes = await axios.get<IndexResponse>(`${API_BASE_URL}/media/index.json`);
                const idx = indexRes.data || {};
                const timestamps = Object.keys(idx).sort((a, b) => (a < b ? 1 : -1));
                const items: ArchiveItem[] = timestamps
                    .map((ts) => {
                        const outputPath = idx[ts]?.output_path;
                        if (!outputPath) {
                            return null;
                        }
                        const item: ArchiveItem = {id: ts, timestamp: ts, output_path: outputPath};
                        return item;
                    })
                    .filter((x): x is ArchiveItem => Boolean(x));
                setArchiveItems(items);
                setArchiveError("");

                if (timestamps.length > 0) {
                    const latestId = timestamps[0];
                    try {
                        const latest = await fetchOutputById(latestId, idx);
                        setOutputStatus(latest ?? null);
                    } catch (e) {
                        console.error(e);
                        setOutputStatus(null);
                    }
                } else {
                    setOutputStatus(null);
                }
            } catch (e) {
                console.error(e);
                setArchiveItems([]);
                setArchiveError("Errore caricamento archive.");
            }
        };

        fetchArchive();
    }, []);
    // Refs per accedere allo stato corrente dentro il refresh periodico (evita closure stantie)
    const recordsByIdRef = useRef(recordsById);
    const outputStatusRef = useRef(outputStatus);
    useEffect(() => {
        recordsByIdRef.current = recordsById;
    }, [recordsById]);
    useEffect(() => {
        outputStatusRef.current = outputStatus;
    }, [outputStatus]);

    // Refresh "soft": riscarica l'index e ricarica i dati già aperti (record caricati,
    // rilevazione mostrata, ultima disponibile) senza ricaricare la pagina.
    const refreshData = useCallback(async () => {
        try {
            const indexRes = await axios.get<IndexResponse>(`${API_BASE_URL}/media/index.json`);
            const idx = indexRes.data || {};
            const timestamps = Object.keys(idx).sort((a, b) => (a < b ? 1 : -1));
            const items: ArchiveItem[] = timestamps
                .map((ts) => {
                    const outputPath = idx[ts]?.output_path;
                    if (!outputPath) {
                        return null;
                    }
                    const item: ArchiveItem = {id: ts, timestamp: ts, output_path: outputPath};
                    return item;
                })
                .filter((x): x is ArchiveItem => Boolean(x));
            setArchiveItems(items);
            setArchiveError("");

            // Id da ricaricare: record già caricati + rilevazione mostrata + più recente
            const idsToReload = new Set<string>(
                Object.keys(recordsByIdRef.current).filter((id) => recordsByIdRef.current[id])
            );
            const currentId = outputStatusRef.current?.id;
            if (currentId) {
                idsToReload.add(currentId);
            }
            const latestId = timestamps[0];
            if (latestId) {
                idsToReload.add(latestId);
            }

            const results = await Promise.allSettled(
                Array.from(idsToReload).map(async (id): Promise<ArchiveRecord | null> => {
                    const outputPath = idx[id]?.output_path;
                    if (!outputPath) {
                        return null;
                    }
                    const url = toMediaUrl(outputPath);
                    if (!url) {
                        return null;
                    }
                    const res = await axios.get<RawOutput>(url);
                    const out = parseOutputJson(res.data, url);
                    if (!out) {
                        return null;
                    }
                    return {id, ...out};
                })
            );

            const fresh: Record<string, ArchiveRecord> = {};
            for (const r of results) {
                if (r.status === "fulfilled" && r.value) {
                    fresh[r.value.id] = r.value;
                }
            }
            if (Object.keys(fresh).length > 0) {
                setRecordsById((prev) => ({...prev, ...fresh}));
            }

            // Aggiorna la rilevazione mostrata con i dati freschi
            if (currentId && fresh[currentId]) {
                setOutputStatus(fresh[currentId]);
            } else if (!currentId && latestId && fresh[latestId]) {
                setOutputStatus(fresh[latestId]);
            }
        } catch (e) {
            console.error(e);
        }
        // toMediaUrl dipende solo da costanti: stabile tra i render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            refreshData();
        }, 5 * 60 * 1000);

        return () => clearInterval(id);
    }, [refreshData]);

    const carouselImagesByStation = normalizeImagesByStation(outputStatus?.lista_path_immagini);

    const onOpenDay = async (dayKey: string, items: ArchiveItem[]) => {
        const current = dayStateByKey[dayKey];
        if (current?.loading || current?.loaded) {
            return;
        }
        setDayStateByKey((prev) => ({...prev, [dayKey]: {loading: true, loaded: false, error: ""}}));
        try {
            const toFetch = items.filter((i) => !recordsById[i.id]);
            const results = await Promise.allSettled(
                toFetch.map(async (i) => {
                    const url = toMediaUrl(i.output_path);
                    if (!url) {
                        return null;
                    }
                    const res = await axios.get<RawOutput>(url);
                    const out = parseOutputJson(res.data, url);
                    if (!out) {
                        return null;
                    }
                    const rec: ArchiveRecord = {id: i.id, ...out};
                    return rec;
                })
            );
            const loaded: ArchiveRecord[] = [];
            for (const r of results) {
                if (r.status !== "fulfilled" || !r.value) {
                    continue;
                }
                loaded.push(r.value);
            }
            if (loaded.length > 0) {
                setRecordsById((prev) => {
                    const next = {...prev};
                    for (const rec of loaded) {
                        if (!next[rec.id]) {
                            next[rec.id] = rec;
                        }
                    }
                    return next;
                });
            }
            const hasAny = items.some((i) => Boolean(recordsById[i.id])) || loaded.length > 0;
            setDayStateByKey((prev) => ({
                ...prev,
                [dayKey]: {
                    loading: false,
                    loaded: true,
                    error: hasAny ? "" : "Errore: nessun output.json caricato per questo giorno.",
                },
            }));
        } catch (e) {
            console.error(e);
            setDayStateByKey((prev) => ({
                ...prev,
                [dayKey]: {loading: false, loaded: true, error: "Errore caricamento output.json del giorno."},
            }));
        }
    };

    return (
        <div className="app-shell">
            <SynopticPanel
                outputStatus={outputStatus}
                outputError={outputError}
                radarThreshold={radarThreshold}
                sensorsThreshold={sensorsThreshold}
                mediaThreshold={mediaThreshold}
                getStatusColor={getStatusColor}
                formatValue={formatValue}
                openLinksPage={openLinksPage}
                onOpenEmail={(e) => setSelectedEmail(e)}
            />

            <main className="right-panel">
                <div className="panel-header">
                    <h2>Archivio rilevazioni</h2>
                    <div className="panel-subtitle">Storico</div>
                </div>

                <ImageCarousel
                    imagesByStation={carouselImagesByStation}
                    toMediaUrl={toMediaUrl}
                    openLinksPage={openLinksPage}
                />

                <ArchiveView
                    archiveItems={archiveItems}
                    archiveError={archiveError}
                    recordsById={recordsById}
                    dayStateByKey={dayStateByKey}
                    onOpenDay={onOpenDay}
                    radarThreshold={radarThreshold}
                    sensorsThreshold={sensorsThreshold}
                    mediaThreshold={mediaThreshold}
                    getStatusColor={getStatusColor}
                    formatValue={formatValue}
                    onSelectRecord={(r) => setOutputStatus(r)}
                />
            </main>

            {selectedEmail ? <EmailModal email={selectedEmail} onClose={() => setSelectedEmail(null)} /> : null}
        </div>
    );
};

export default App;
